import { FormFieldError } from '@tovala/component-library'
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { useEffect, useRef, useState } from 'react'

import { hasFailedToLoadStripe } from 'utils/stripe'

import ErrorDisplay from './ErrorDisplay'

const ELEMENT_STYLE = {
  base: {
    color: '#6F6F6A',
    fontFamily: 'Sohne, sans-serif',
    fontSize: '14px',
    letterSpacing: '0.02em',
    '::placeholder': { color: '#6F6F6A' },
  },
}

const CreditCardInput = ({
  onChange,
  postalCode: postalCodeProp,
}: {
  onChange?: (status: { complete: boolean; postalCode: string }) => void
  postalCode?: string
}) => {
  // We need separate state for checking if Stripe failed to load since the
  // "stripe" variable from "useStripe" will be null while Stripe is loading.
  const [failedToLoadStripe, setFailedToLoadStripe] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  const [cardNumberComplete, setCardNumberComplete] = useState(false)
  const [expiryComplete, setExpiryComplete] = useState(false)
  const [cvcComplete, setCvcComplete] = useState(false)
  const [postalCode, setPostalCode] = useState('')
  const [zipTouched, setZipTouched] = useState(false)

  useEffect(() => {
    if (postalCodeProp !== undefined) {
      setPostalCode(postalCodeProp)
    }
  }, [postalCodeProp])

  const zipInputRef = useRef<HTMLInputElement | null>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const elements = useElements()
  const stripe = useStripe()

  useEffect(() => {
    if (stripe === null) {
      setFailedToLoadStripe(hasFailedToLoadStripe())
    }
  }, [stripe])

  useEffect(() => {
    const isZipValid = /^\d{5}$/.test(postalCode)
    onChangeRef.current?.({
      complete:
        cardNumberComplete && expiryComplete && cvcComplete && isZipValid,
      postalCode,
    })
  }, [cardNumberComplete, expiryComplete, cvcComplete, postalCode])

  const showZipError =
    zipTouched && postalCode.length > 0 && !/^\d{5}$/.test(postalCode)

  return (
    <div className="space-y-4">
      {failedToLoadStripe ? (
        <ErrorDisplay
          helpToFix="You may have a browser extension blocking our script, but please reload the page to try again."
          why="We can't add a new card to your account because we couldn't load our payment processor."
        />
      ) : (
        <div className="space-y-2">
          <div
            className="cursor-text rounded-lg border border-grey-4 bg-grey-0 p-4"
            onClick={() => {
              elements?.getElement(CardNumberElement)?.focus()
            }}
          >
            <CardNumberElement
              onChange={(event) => {
                setCardNumberComplete(event.complete)
                setValidationError(event.error?.message ?? null)
                if (event.complete) {
                  elements?.getElement(CardExpiryElement)?.focus()
                }
              }}
              options={{ style: ELEMENT_STYLE }}
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div
              className="cursor-text rounded-lg border border-grey-4 bg-grey-0 p-4"
              onClick={() => {
                elements?.getElement(CardExpiryElement)?.focus()
              }}
            >
              <CardExpiryElement
                onChange={(event) => {
                  setExpiryComplete(event.complete)
                  setValidationError(event.error?.message ?? null)
                  if (event.complete) {
                    elements?.getElement(CardCvcElement)?.focus()
                  }
                }}
                options={{ style: ELEMENT_STYLE }}
              />
            </div>

            <div
              className="cursor-text rounded-lg border border-grey-4 bg-grey-0 p-4"
              onClick={() => {
                elements?.getElement(CardCvcElement)?.focus()
              }}
            >
              <CardCvcElement
                onChange={(event) => {
                  setCvcComplete(event.complete)
                  setValidationError(event.error?.message ?? null)
                  if (event.complete) {
                    zipInputRef.current?.focus()
                  }
                }}
                options={{ style: ELEMENT_STYLE }}
              />
            </div>

            <input
              ref={zipInputRef}
              autoComplete="postal-code"
              className="w-full rounded-lg border border-grey-4 bg-grey-0 p-4 text-h/14_120 text-grey-9 placeholder:text-grey-9"
              inputMode="numeric"
              maxLength={5}
              onBlur={() => setZipTouched(true)}
              onChange={(e) => {
                const numeric = e.target.value.replace(/\D/g, '').slice(0, 5)
                setPostalCode(numeric)
              }}
              onFocus={() => setZipTouched(false)}
              placeholder="ZIP"
              type="text"
              value={postalCode}
            />
          </div>

          {validationError && (
            <FormFieldError>{validationError}</FormFieldError>
          )}
          {showZipError && (
            <FormFieldError>
              Please enter a valid 5-digit ZIP code
            </FormFieldError>
          )}
        </div>
      )}
    </div>
  )
}

export default CreditCardInput
